/**
 * โมเดลตัวแทนของมอนที่ยังไม่มีโมเดลวาดเอง — รูปร่างตามเผ่า (race) สีจาก `look` วงแสงใต้ตัวตามธาตุ
 *
 * มอนชุดใหม่ (PLAN ขั้น 5) มีร้อยกว่าชนิด ถ้าทุกตัวเป็นก้อนกลมมีตาเหมือนกันหมด แยกไม่ออกว่าตีอะไรอยู่
 * ที่นี่จึงประกอบรูปทรงพื้นฐานตามเผ่า: สัตว์สี่ขา แมลงหกขา พืช สัตว์น้ำ อันเดด วิญญาณ สิ่งประดิษฐ์ มังกร มนุษย์
 * เมื่อมีไฟล์ .glb ของชนิดนั้น `upgradeMonster` ซ่อนลูกของ body แล้วใส่โมเดลจริงแทนเหมือนเดิม
 * **ใช้วัสดุจาก `mat` ที่ส่งเข้ามา** (แคชต่อฉากของ models.ts) ห้ามสร้างวัสดุเอง
 */

import type { Scene } from "@babylonjs/core/scene";
import type { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { Element, Race } from "@wickhaven/shared";

type MatFn = (hex: string, opts?: { em?: string; alpha?: number; noLight?: boolean }) => StandardMaterial;

import { ELEMENT_GLOW } from "./element-colors.js";
export { ELEMENT_GLOW };

/** ปรับสีเข้ม/อ่อน ใช้ทำส่วนรองของตัว (ขา เขา) จากสีหลัก */
function shade(hex: string, amt: number): string {
  const n = parseInt(hex.replace("#", ""), 16);
  const ch = (v: number): string => {
    const o = amt < 0 ? v * (1 + amt) : v + (255 - v) * amt;
    return Math.round(Math.max(0, Math.min(255, o))).toString(16).padStart(2, "0");
  };
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

export interface RaceModelResult {
  shadowR: number;
  centerY: number;
}

/**
 * ประกอบตัวมอนลงใต้ `body` (body ขยับตามท่าใน animateMonster อยู่แล้ว)
 * `k` = ขนาดจาก look.size · หน้าหันไป +Z ตามข้อตกลงของเกม
 */
export function buildRaceModel(
  scene: Scene, root: TransformNode, body: TransformNode, race: Race | undefined, element: Element | undefined,
  color: string, k: number, float: boolean, boss: boolean, mat: MatFn, flat: (m: Mesh) => Mesh,
  isDummy = false,
): RaceModelResult {
  const dark = shade(color, -0.35);
  const light = shade(color, 0.3);
  const glow = ELEMENT_GLOW[element ?? "neutral"];
  const part = (m: Mesh, hex: string, x: number, y: number, z: number, opts?: Parameters<MatFn>[1]): Mesh => {
    flat(m);
    m.position.set(x * k, y * k, z * k);
    m.material = mat(hex, opts);
    m.parent = body;
    return m;
  };
  const sphere = (d: number, segments = 4): Mesh => MeshBuilder.CreateSphere("rp", { diameter: d * k, segments }, scene);
  const box = (w: number, h: number, d: number): Mesh => MeshBuilder.CreateBox("rp", { width: w * k, height: h * k, depth: d * k }, scene);
  const cyl = (top: number, bot: number, h: number, t = 5): Mesh =>
    MeshBuilder.CreateCylinder("rp", { diameterTop: top * k, diameterBottom: bot * k, height: h * k, tessellation: t }, scene);
  const eyePair = (y: number, z: number, sep: number, size = 0.13, hex = "#1a1a1a", em?: string): void => {
    for (const x of [-sep, sep]) part(sphere(size, 3), hex, x, y, z, em ? { em, noLight: true } : undefined);
  };

  let lift = float ? 1.0 : 0;
  let shadowR = 0.5 * k;
  let centerY = 0.5 * k;

  switch (race) {
    case "beast": {
      const torso = part(sphere(0.9), color, 0, 0.62, 0);
      torso.scaling.set(0.9, 0.75, 1.3);
      part(sphere(0.55), color, 0, 0.92, 0.55);
      part(sphere(0.25, 3), light, 0, 0.84, 0.82); // ปาก
      eyePair(1.0, 0.76, 0.13);
      for (const x of [-0.17, 0.17]) part(cyl(0, 0.16, 0.26, 4), dark, x, 1.22, 0.5); // หู
      for (const [x, z] of [[-0.24, 0.32], [0.24, 0.32], [-0.24, -0.32], [0.24, -0.32]]) part(cyl(0.14, 0.17, 0.42), dark, x!, 0.21, z!);
      const tail = part(cyl(0.04, 0.14, 0.5, 4), dark, 0, 0.75, -0.62);
      tail.rotation.x = -0.9;
      if (float) { // ตัวที่บินได้: ปีกนก
        for (const d of [-1, 1]) { const w = part(box(0.9, 0.05, 0.45), light, d * 0.55, 0.9, 0); w.rotation.z = d * 0.25; }
      }
      shadowR = 0.6 * k; centerY = 0.65 * k;
      break;
    }
    case "insect": {
      for (const [z, d] of [[0.42, 0.42], [0.05, 0.55], [-0.38, 0.65]]) {
        const s = part(sphere(d!), color, 0, 0.45, z!); s.scaling.y = 0.75;
      }
      eyePair(0.55, 0.6, 0.12, 0.15, "#ff3a3a");
      for (const x of [-0.08, 0.08]) { const a = part(cyl(0.02, 0.04, 0.5, 3), dark, x, 0.82, 0.55); a.rotation.x = 0.6; }
      for (const z of [0.25, 0, -0.25]) for (const d of [-1, 1]) {
        const leg = part(cyl(0.04, 0.06, 0.55, 3), dark, d * 0.36, 0.25, z); leg.rotation.z = d * 1.0;
      }
      if (float) for (const d of [-1, 1]) {
        const w = part(box(0.7, 0.03, 0.35), "#e8f4ff", d * 0.4, 0.8, 0, { alpha: 0.6 }); w.rotation.z = d * 0.4;
      }
      shadowR = 0.6 * k; centerY = 0.45 * k;
      break;
    }
    case "plant": {
      const bulb = part(sphere(0.85), color, 0, 0.5, 0); bulb.scaling.y = 1.1;
      eyePair(0.62, 0.38, 0.14);
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        const leaf = part(sphere(0.45, 3), dark, Math.cos(a) * 0.45, 0.15, Math.sin(a) * 0.45);
        leaf.scaling.set(1, 0.2, 0.55); leaf.rotation.y = -a;
      }
      part(cyl(0.05, 0.08, 0.35, 4), dark, 0, 1.05, 0);
      part(sphere(0.32, 4), light, 0, 1.28, 0, { em: shade(light, -0.5) }); // ดอกบนหัว
      shadowR = 0.55 * k; centerY = 0.5 * k;
      break;
    }
    case "aquatic": {
      const sh = part(sphere(1), color, 0, 0.42, 0); sh.scaling.set(1.2, 0.55, 1);
      eyePair(0.75, 0.3, 0.16, 0.16);
      for (const x of [-0.16, 0.16]) part(cyl(0.05, 0.05, 0.25, 4), dark, x, 0.62, 0.3);
      for (const d of [-1, 1]) { // ก้าม/ครีบ
        const cl = part(sphere(0.38, 3), light, d * 0.68, 0.38, 0.32); cl.scaling.set(1, 0.6, 0.8);
        const arm = part(cyl(0.06, 0.08, 0.35, 4), dark, d * 0.5, 0.33, 0.2); arm.rotation.z = d * 1.2;
      }
      shadowR = 0.65 * k; centerY = 0.42 * k;
      break;
    }
    case "undead": {
      part(cyl(0.38, 0.5, 0.75, 6), color, 0, 0.75, 0);
      part(sphere(0.48), light, 0, 1.38, 0);
      eyePair(1.4, 0.2, 0.1, 0.12, glow, glow);
      for (const d of [-1, 1]) { const arm = part(cyl(0.08, 0.1, 0.65, 4), light, d * 0.33, 0.85, 0.12); arm.rotation.x = -1.0; }
      for (const x of [-0.13, 0.13]) part(cyl(0.1, 0.12, 0.4, 4), dark, x, 0.2, 0);
      shadowR = 0.45 * k; centerY = 0.85 * k;
      break;
    }
    case "spirit": {
      lift = Math.max(lift, 0.6);
      const core = part(sphere(0.8), color, 0, 0.6, 0, { alpha: 0.85, em: shade(color, -0.4) });
      core.scaling.y = 1.15;
      const tail = part(cyl(0.45, 0.02, 0.7, 6), color, 0, 0.05, -0.05, { alpha: 0.7 });
      tail.rotation.x = 0.25;
      eyePair(0.7, 0.33, 0.13, 0.13, "#ffffff", "#ffffff");
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        part(sphere(0.14, 3), glow, Math.cos(a) * 0.6, 0.75, Math.sin(a) * 0.6, { em: glow, noLight: true });
      }
      shadowR = 0.4 * k; centerY = 0.6 * k;
      break;
    }
    case "construct": {
      if (isDummy) {
        // หุ่นฟางฝึกซ้อมสไตล์ Ragnarok Online / Chibi Fantasy MMORPG
        // 1. ฐานไม้กากบาทค้ำยันบนพื้น
        part(box(0.9, 0.08, 0.22), "#5c4033", 0, 0.04, 0);
        part(box(0.22, 0.08, 0.9), "#5c4033", 0, 0.04, 0);
        // 2. เสาไม้แกนตั้ง
        part(cyl(0.14, 0.16, 1.75, 6), "#6e4d2e", 0, 0.88, 0);
        // 3. คานไม้ขวางกางแขน
        part(cyl(0.12, 0.12, 1.3, 6), "#6e4d2e", 0, 1.15, 0);
        // 4. ลำตัวมัดฟางข้าวสีทอง
        part(cyl(0.55, 0.48, 0.8, 7), "#dfb74a", 0, 0.85, 0);
        // 5. เชือกป่านมัดลำตัว
        part(cyl(0.57, 0.57, 0.06, 8), "#6d4c2b", 0, 0.95, 0);
        part(cyl(0.52, 0.52, 0.06, 8), "#6d4c2b", 0, 0.72, 0);
        // 6. เป้าซ้อมประลองทรงกลมที่หน้าอก (ขาว-แดง-ทอง)
        const targetOuter = part(cyl(0.38, 0.38, 0.05, 12), "#f5f5f0", 0, 0.85, 0.26);
        targetOuter.rotation.x = Math.PI / 2;
        const targetRed = part(cyl(0.26, 0.26, 0.06, 12), "#d32f2f", 0, 0.85, 0.27);
        targetRed.rotation.x = Math.PI / 2;
        const targetCenter = part(cyl(0.12, 0.12, 0.07, 10), "#ffca28", 0, 0.85, 0.28, { em: "#ffb300", noLight: true });
        targetCenter.rotation.x = Math.PI / 2;
        // 7. มัดฟางปลายแขนสองข้าง
        for (const d of [-1, 1]) {
          const armStraw = part(cyl(0.18, 0.08, 0.35, 5), "#dfb74a", d * 0.65, 1.15, 0);
          armStraw.rotation.z = -d * Math.PI / 2;
          part(cyl(0.19, 0.19, 0.04, 6), "#6d4c2b", d * 0.55, 1.15, 0);
        }
        // 8. หัวมัดฟาง
        part(sphere(0.48, 5), "#ecd269", 0, 1.45, 0);
        // หมวกฟาง Chibi
        part(cyl(0, 0.68, 0.28, 7), "#c4993a", 0, 1.72, 0);
        // ผ้ามัดตา/ผ้าโพกสีแดง
        part(cyl(0.5, 0.5, 0.1, 8), "#e53935", 0, 1.48, 0);
        // ตาเย็บกากบาท
        eyePair(1.48, 0.23, 0.12, 0.07, "#2e1c0c");
        shadowR = 0.55 * k; centerY = 0.85 * k;
      } else {
        part(box(0.85, 0.75, 0.6), color, 0, 0.75, 0);
        part(box(0.5, 0.42, 0.45), light, 0, 1.38, 0.02);
        part(box(0.32, 0.08, 0.05), glow, 0, 1.42, 0.25, { em: glow, noLight: true }); // ตาแถบเรืองแสง
        for (const d of [-1, 1]) {
          part(box(0.3, 0.7, 0.32), dark, d * 0.6, 0.65, 0);
          part(box(0.26, 0.4, 0.3), dark, d * 0.22, 0.2, 0);
        }
        part(sphere(0.22, 3), glow, 0, 0.8, 0.3, { em: glow, noLight: true }); // แกนกลางอก
        shadowR = 0.65 * k; centerY = 0.75 * k;
      }
      break;
    }
    case "dragon": {
      const torso = part(sphere(1), color, 0, 0.75, 0); torso.scaling.set(0.85, 0.75, 1.35);
      const neck = part(cyl(0.25, 0.4, 0.6, 5), color, 0, 1.15, 0.5); neck.rotation.x = 0.6;
      part(sphere(0.5), color, 0, 1.45, 0.78);
      part(box(0.28, 0.2, 0.35), light, 0, 1.38, 1.02);
      eyePair(1.55, 0.95, 0.14, 0.12, "#ffd23f", "#ffb000");
      for (const x of [-0.14, 0.14]) { const h = part(cyl(0, 0.1, 0.4, 4), "#f0e8d8", x, 1.78, 0.68); h.rotation.x = -0.5; }
      for (const d of [-1, 1]) { const w = part(cyl(0, 0.9, 1.2, 3), dark, d * 0.75, 1.2, -0.1, { alpha: 0.95 }); w.rotation.z = d * 1.25; w.scaling.z = 0.15; }
      const tail = part(cyl(0.05, 0.35, 1.1, 5), color, 0, 0.6, -0.95); tail.rotation.x = -1.2;
      for (const [x, z] of [[-0.3, 0.35], [0.3, 0.35], [-0.3, -0.3], [0.3, -0.3]]) part(cyl(0.16, 0.2, 0.45), dark, x!, 0.22, z!);
      shadowR = 0.8 * k; centerY = 0.8 * k;
      break;
    }
    case "humanoid":
    default: {
      part(cyl(0.42, 0.55, 0.75, 6), color, 0, 0.78, 0);
      part(sphere(0.5), light, 0, 1.42, 0);
      eyePair(1.45, 0.22, 0.1, 0.1);
      part(cyl(0, 0.62, 0.4, 6), dark, 0, 1.75, 0); // หมวก/ผ้าคลุมหัว
      for (const d of [-1, 1]) { const arm = part(cyl(0.1, 0.13, 0.62, 5), color, d * 0.38, 0.85, 0.05); arm.rotation.z = d * 0.25; }
      for (const x of [-0.14, 0.14]) part(cyl(0.13, 0.15, 0.42, 5), dark, x, 0.21, 0);
      shadowR = 0.45 * k; centerY = 0.85 * k;
    }
  }

  // บอส: มงกุฎธาตุบนหัว ให้เห็นแต่ไกลว่าตัวนี้ไม่ธรรมดา
  if (boss) {
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2;
      part(cyl(0, 0.12, 0.3, 4), glow, Math.cos(a) * 0.22, centerY / k + 0.95, Math.sin(a) * 0.22, { em: glow, noLight: true });
    }
  }

  body.position.y = lift;
  // วงแสงธาตุบนพื้น (อยู่ใต้ root ไม่ขยับตามท่า)
  const ring = MeshBuilder.CreateTorus("elemRing", { diameter: shadowR * 2.3, thickness: 0.06 * Math.max(1, k * 0.7), tessellation: 20 }, scene);
  ring.position.y = 0.04;
  ring.material = mat(glow, { em: glow, alpha: 0.7, noLight: true });
  ring.parent = root;
  return { shadowR, centerY: centerY + lift };
}
